import { createFileRoute, Link } from "@tanstack/react-router";
import { seoHead } from "@/lib/seo";
import { useCart, formatUsd, FREE_SHIPPING_THRESHOLD } from "@/lib/cart";
import { SHIPPING_NOTE } from "@/lib/shop-products";
import { Trash2 } from "lucide-react";

export const Route = createFileRoute("/cart")({
  head: () =>
    seoHead({
      path: "/cart",
      title: "Your Cart — Coffman Cove Lodging Shop",
      description:
        "Review the lodge shop items in your cart before checkout. Everything ships directly to your door in the lower 48 states.",
      extraMeta: [{ name: "robots", content: "noindex, follow" }],
    }),
  component: CartPage,
});

function CartPage() {
  const { lines, count, subtotal, shipping, total, hydrated, setQty, remove } =
    useCart();

  return (
    <div className="mx-auto max-w-5xl px-4 sm:px-6 py-12 sm:py-16">
      <p className="text-[10px] uppercase tracking-[0.3em] text-[var(--color-ember)]">
        Lodge Shop
      </p>
      <h1 className="mt-2 font-display text-4xl sm:text-5xl">Your cart</h1>

      {!hydrated ? (
        <p className="mt-8 text-muted-foreground">Loading your cart…</p>
      ) : count === 0 ? (
        <div className="mt-8 rounded-xl border border-border bg-card p-8 text-center">
          <p className="font-display text-2xl">Your cart is empty.</p>
          <p className="mt-2 text-muted-foreground">
            Tees, hoodies, hats, engraved knives and Alaska keepsakes — all packed at the lodge.
          </p>
          <Link
            to="/shop"
            className="mt-6 inline-block rounded-md bg-[var(--color-ember)] px-6 py-3 text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90"
          >
            Browse the shop
          </Link>
        </div>
      ) : (
        <div className="mt-8 grid lg:grid-cols-[1fr_20rem] gap-10">
          <ul className="divide-y divide-border rounded-xl border border-border bg-card">
            {lines.map(({ key, product, qty, variantLabel }) => (
              <li key={key} className="flex gap-4 p-4 sm:p-5">
                <Link
                  to="/shop/$productId"
                  params={{ productId: product.id }}
                  className="h-20 w-20 shrink-0 overflow-hidden rounded-md bg-[var(--color-navy-deep)]"
                  aria-label={`View ${product.name}`}
                >
                  {product.image && (
                    <img
                      src={product.image}
                      alt={product.name}
                      loading="lazy"
                      className="h-full w-full object-cover"
                    />
                  )}
                </Link>
                <div className="min-w-0 flex-1">
                  <Link
                    to="/shop/$productId"
                    params={{ productId: product.id }}
                    className="font-display text-lg hover:text-[var(--color-ember)]"
                  >
                    {product.name}
                  </Link>
                  {variantLabel && (
                    <p className="text-sm text-muted-foreground">{variantLabel}</p>
                  )}
                  <p className="text-sm text-muted-foreground">
                    {formatUsd(product.price)} each
                  </p>
                  <div className="mt-3 flex flex-wrap items-center gap-3">
                    <div className="inline-flex items-center rounded-md border border-border">
                      <button
                        onClick={() => setQty(key, qty - 1)}
                        className="px-3 py-1.5 text-lg hover:text-[var(--color-ember)]"
                        aria-label={`Decrease quantity of ${product.name}${variantLabel ? ` (${variantLabel})` : ""}`}
                      >
                        −
                      </button>
                      <span className="w-9 text-center text-sm font-semibold">{qty}</span>
                      <button
                        onClick={() => setQty(key, qty + 1)}
                        className="px-3 py-1.5 text-lg hover:text-[var(--color-ember)]"
                        aria-label={`Increase quantity of ${product.name}${variantLabel ? ` (${variantLabel})` : ""}`}
                      >
                        +
                      </button>
                    </div>
                    <button
                      onClick={() => remove(key)}
                      className="inline-flex items-center gap-1.5 text-xs uppercase tracking-wider text-muted-foreground hover:text-[var(--color-ember)]"
                    >
                      <Trash2 className="h-3.5 w-3.5" /> Remove
                    </button>
                  </div>
                </div>
                <p className="shrink-0 font-semibold">
                  {formatUsd(product.price * qty)}
                </p>
              </li>
            ))}
          </ul>

          <aside className="h-fit rounded-xl border border-border bg-card p-5">
            <h2 className="font-display text-xl">Order summary</h2>
            <dl className="mt-4 space-y-2 text-sm">
              <div className="flex justify-between">
                <dt className="text-muted-foreground">
                  Subtotal ({count} item{count === 1 ? "" : "s"})
                </dt>
                <dd className="font-semibold">{formatUsd(subtotal)}</dd>
              </div>
              <div className="flex justify-between">
                <dt className="text-muted-foreground">Shipping (lower 48)</dt>
                <dd className="font-semibold">
                  {shipping === 0 ? "Free" : formatUsd(shipping)}
                </dd>
              </div>
              <div className="flex justify-between border-t border-border pt-3 text-base">
                <dt className="font-semibold">Total</dt>
                <dd className="font-semibold">{formatUsd(total)}</dd>
              </div>
            </dl>
            {shipping > 0 && (
              <p className="mt-3 text-xs text-muted-foreground">
                Add {formatUsd(FREE_SHIPPING_THRESHOLD - subtotal)} more for free shipping.
              </p>
            )}
            <Link
              to="/checkout"
              className="mt-5 block rounded-md bg-[var(--color-ember)] px-6 py-3 text-center text-sm font-semibold uppercase tracking-wider text-[var(--color-ember-foreground)] hover:opacity-90"
            >
              Checkout
            </Link>
            <Link
              to="/shop"
              className="mt-3 block rounded-md border border-border px-6 py-3 text-center text-sm font-semibold uppercase tracking-wider hover:border-[var(--color-ember)] hover:text-[var(--color-ember)]"
            >
              Keep shopping
            </Link>
            <p className="mt-4 text-xs text-muted-foreground">{SHIPPING_NOTE}</p>
          </aside>
        </div>
      )}
    </div>
  );
}
